import React, { useEffect, useState, useRef, useContext } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon, LogOutIcon, UserIcon, HomeIcon, BookOpenIcon, CalendarIcon, MessageSquareIcon, GraduationCapIcon, UsersIcon, ClockIcon, InfoIcon, SunIcon, MoonIcon, SearchIcon, SettingsIcon, ShieldIcon, CreditCardIcon, HelpCircleIcon } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SignInModalContext } from '../App';
import { NotificationCenter } from './NotificationCenter';
import { useTheme } from './providers/ThemeProvider';
export const Navbar = ({
  setIsDeleteAccountModalOpen
}) => {
  const {
    currentUser,
    userData,
    userType,
    logout
  } = useAuth();
  const {
    openTeacherLoginModal
  } = useContext(SignInModalContext);
  const {
    theme,
    setTheme
  } = useTheme();
  const isDark = theme === 'dark';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // ---- FIXED: Clean userType normalization ----
  const normalizedUserType = userType ? userType.toLowerCase().trim() : null;
  const isStudent = normalizedUserType === 'student';
  const isTeacher = normalizedUserType === 'teacher';
  // ---- FIXED: Single source of truth for dashboard routing ----
  let dashboardPath = '/';
  if (isStudent) {
    dashboardPath = '/student-dashboard';
  } else if (isTeacher) {
    dashboardPath = '/teacher-dashboard';
  }
  // ---- FIXED: Single source of truth for profile routing ----
  let profilePath = '/';
  if (isStudent) {
    profilePath = '/my-profile';
  } else if (isTeacher) {
    profilePath = '/teacher-profile';
  }
  // ---- FIXED: Single source of truth for calendar routing ----
  let calendarPath = '/';
  if (isStudent) {
    calendarPath = '/student-schedule';
  } else if (isTeacher) {
    calendarPath = '/teacher/calendar';
  }
  // ---- FIXED: Single source of truth for messages routing ----
  let messagesPath = '/';
  if (isStudent) {
    messagesPath = '/messages';
  } else if (isTeacher) {
    messagesPath = '/teacher-messages';
  }
  // Toggle theme
  const toggleTheme = () => {
    setTheme(isDark ? 'light' : 'dark');
  };
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  // ---- FIXED: Dedicated Dashboard click handler ----
  const handleDashboardClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (!dashboardPath || dashboardPath === '/') {
      // Authenticated but no role → safe fallback to student dashboard
      navigate('/student-dashboard');
      return;
    }
    // Navigate to the correct dashboard based on userType
    navigate(dashboardPath);
    window.scrollTo(0, 0);
  };
  // ---- FIXED: Dedicated Profile click handler ----
  const handleProfileClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(profilePath); // student profile route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(profilePath); // teacher profile route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Calendar click handler ----
  const handleCalendarClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(calendarPath); // student calendar/schedule route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(calendarPath); // teacher calendar route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // ---- FIXED: Dedicated Messages click handler ----
  const handleMessagesClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!currentUser) {
      // Not authenticated → go home
      navigate('/');
      return;
    }
    if (isStudent) {
      navigate(messagesPath); // student messages route
      window.scrollTo(0, 0);
      return;
    }
    if (isTeacher) {
      navigate(messagesPath); // teacher messages route
      window.scrollTo(0, 0);
      return;
    }
    // Fallback if userType is unknown
    navigate('/');
  };
  // Check if user is logged in based on Firebase auth
  useEffect(() => {
    const checkUserLoggedIn = () => {
      // STEP 1 FIX: Use Firebase currentUser as the SOLE source of truth
      // Removed the isOnDashboardPages() fallback that caused desync on cross-tab logout
      if (currentUser) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
      // Set loading to false after checking
      setIsLoading(false);
    };
    checkUserLoggedIn();
  }, [currentUser, location.pathname]);
  // Check if current location is a dashboard/logged-in page
  const isOnDashboardPages = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/messages' || location.pathname === '/student-schedule' || location.pathname === '/online-courses' || location.pathname === '/find-teachers' || location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/my-students' || location.pathname === '/my-availability' || location.pathname.startsWith('/teacher/') || isOnMyAccountPage();
  };
  // Check if current location is a My Account page
  const isOnMyAccountPage = () => {
    return location.pathname === '/my-info' || location.pathname === '/settings' || location.pathname === '/account-security' || location.pathname === '/billing-payouts' || location.pathname === '/help-support';
  };
  // Check if current location is a teacher dashboard page
  const isOnTeacherDashboardPage = () => {
    return location.pathname === '/teacher-dashboard' || location.pathname === '/teacher-profile' || location.pathname === '/teacher-classes' || location.pathname === '/reports' || location.pathname === '/my-availability' || location.pathname === '/my-students' || location.pathname === '/messages' || location.pathname === '/teacher-messages' || location.pathname === '/assigned-tasks' || location.pathname === '/teacher-sheet-music' || location.pathname === '/teacher/calendar2' || location.pathname.startsWith('/student-profile/') || isOnMyAccountPage();
  };
  // Check if current location is a student page
  const isOnStudentPage = () => {
    return location.pathname === '/student-dashboard' || location.pathname === '/find-teachers' || location.pathname === '/student-schedule' || location.pathname === '/messages' || location.pathname === '/online-courses' || location.pathname === '/sheet-music' || location.pathname === '/student-settings' || location.pathname === '/my-profile' || location.pathname === '/student/profile' || location.pathname === '/account-settings' || isOnMyAccountPage();
  };
  useEffect(() => {
    let ticking = false;
    let lastScrolled = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrolled = window.scrollY > 10;
        if (scrolled !== lastScrolled) {
          lastScrolled = scrolled;
          setIsScrolled(scrolled);
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside - only add this effect when dropdown is open
  useEffect(() => {
    if (!isAccountDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false);
      }
    };
    // Add event listener with a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountDropdownOpen]);
  // Close mobile menu when tapping outside — mobile only
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleOutsideClick = (event: Event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    // Small delay so the tap that opened the menu doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMenuOpen]);
  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Always prevent default navigation
    e.preventDefault();
    if (isLoggedIn) {
      // If logged in, navigate to dashboard (not landing page)
      if (dashboardPath && dashboardPath !== '/') {
        navigate(dashboardPath);
      } else {
        // Fallback if dashboardPath is not set
        navigate('/teacher-dashboard');
      }
      // Ensure we scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else if (location.pathname === '/') {
      // If we're already on the homepage and not logged in, just scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If on another page and not logged in, navigate to home
      // Store a flag in localStorage to indicate we should scroll to top after navigation
      localStorage.setItem('scrollToTop', 'true');
      navigate('/');
    }
  };
  const handleFindTeacherClick = () => {
    // Ensure we scroll to top when navigating to student signup
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  };
  const handleNavLinkClick = (sectionId: string) => {
    // If logged in, don't navigate to homepage sections
    if (isLoggedIn) {
      return;
    }
    if (location.pathname === '/') {
      // If already on homepage, just scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    } else {
      // If on another page, navigate to homepage first, then scroll to section
      // We'll use localStorage to remember which section to scroll to after navigation
      localStorage.setItem('scrollToSection', sectionId);
      navigate('/');
    }
    // Close menus
    setIsDesktopMenuOpen(false);
    setIsMenuOpen(false);
  };
  // New function to handle scroll to top for already active pages
  const handleSamePageNavigation = (path: string, e: React.MouseEvent) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Open teacher login modal directly (skip role selection for teacher-only beta)
  const handleOpenSignInModal = () => {
    openTeacherLoginModal();
    setIsMenuOpen(false); // Close mobile menu if open
    setIsDesktopMenuOpen(false); // Close desktop menu if open
  };
  // Check if we need to scroll to a section after navigation
  useEffect(() => {
    if (location.pathname === '/') {
      const sectionId = localStorage.getItem('scrollToSection');
      if (sectionId) {
        // Small delay to ensure the page has loaded
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth'
            });
          }
          localStorage.removeItem('scrollToSection');
        }, 100);
      }
    }
  }, [location.pathname]);
  // Check if we need to scroll to top after navigation
  useEffect(() => {
    if (location.pathname === '/' && localStorage.getItem('scrollToTop') === 'true') {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
        localStorage.removeItem('scrollToTop');
      }, 100);
    }
  }, [location.pathname]);
  const handleLogout = async () => {
    try {
      // Use Firebase logout
      await logout();
      // Redirect to home page
      navigate('/');
      // Close the dropdown
      setIsAccountDropdownOpen(false);
      // Update login state
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  // Safe navigation function that maintains authentication state
  const handleAuthenticatedNavigation = (path: string) => {
    console.log('[MOBILE MENU] handleAuthenticatedNavigation called:', path);
    // Conditional navigation based on user type for specific paths
    if (path === '/messages' && isTeacher) {
      navigate('/teacher-messages');
    } else {
      navigate(path);
    }
    // Force immediate scroll to top
    window.scrollTo(0, 0);
    // Close menu AFTER navigation using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after navigation');
      setIsMenuOpen(false);
      setIsAccountDropdownOpen(false);
    });
  };
  // Helper for menu actions: execute action first, then close menu
  const handleMenuAction = (action: () => void, actionName: string = 'action') => {
    console.log('[MOBILE MENU] handleMenuAction called:', actionName);
    // Execute the action first
    action();
    // Close menu after action completes using requestAnimationFrame
    requestAnimationFrame(() => {
      console.log('[MOBILE MENU] Closing menu after', actionName);
      setIsMenuOpen(false);
    });
  };
  // If still loading, render a simplified navbar to prevent errors
  if (isLoading) {
    return <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-navy-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`} data-id="element-2724">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2725">
          <div className="flex items-center justify-between h-16" data-id="element-2726">
            <div className="flex items-center pl-6 md:pl-12" data-id="element-2727">
              <Link to="/" className="text-2xl font-serif font-bold text-white flex items-center" data-id="element-2728">
                <span data-id="element-2729">MaestroConnect</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>;
  }
  return <nav className={`fixed top-0 left-0 right-0 z-50 ${location.pathname === '/' && !isLoggedIn ? 'bg-navy-800/90 backdrop-blur-md' : 'bg-navy-800/90 backdrop-blur-md'} shadow-lg`} data-id="element-2730">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2731">
        <div className="flex justify-between items-center h-20" data-id="element-2732">
          {/* Logo - Now aligned with main content */}
          <a href="/" className="flex items-center" onClick={handleLogoClick} data-id="element-2733">
            <div className="text-xl md:text-2xl font-serif font-bold text-white" data-id="element-2734">
              <span className="text-deep-pink" data-id="element-2735">Maestro</span>Connect
            </div>
          </a>

          <div className="md:hidden flex items-center gap-3" data-id="element-2736">
            {/* Notification bell for mobile - only show when logged in */}
            {isLoggedIn && <NotificationCenter darkMode={isDark} data-id="element-2737" />}

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="inline-flex items-center justify-center p-3 rounded-md text-gray-400 hover:text-white hover:bg-navy-800 focus:outline-none" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} style={{
            minWidth: '48px',
            minHeight: '48px'
          }} data-id="element-2738">
              {isMenuOpen ? <XIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2739" /> : <MenuIcon className="block h-6 w-6" aria-hidden="true" data-id="element-2740" />}
            </button>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8 justify-center pr-12" data-id="element-2741">
            {isLoggedIn ?
          // Dashboard navigation for logged-in users
          <>
                {isStudent ?
            // Student Navigation - Simplified for all internal pages
            isOnStudentPage() ?
            // Simplified navigation for all student internal pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2742">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2743" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2744">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2745" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/find-teachers', e)} data-id="element-2746">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2747" />
                        Find Teachers
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2748">
                        <NotificationCenter darkMode={isDark} data-id="element-2749" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages */}
                      {isStudent && isOnStudentPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2750">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2751" /> : <SunIcon className="h-5 w-5" data-id="element-2752" />}
                        </button>}
                    </> : location.pathname === '/' ?
            // Simplified navigation for student on landing page
            <>
                      <button onClick={handleDashboardClick} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2753">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2754" />
                        Dashboard
                      </button>
                      <Link to="/student-schedule" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center" data-id="element-2755">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2756" />
                        My Schedule
                      </Link>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2757">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2758" />
                        Find Teachers
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2759">
                        <NotificationCenter darkMode={isDark} data-id="element-2760" />
                      </div>
                      {/* Add dark/light mode toggle for My Account pages */}
                      {isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2761">
                          {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2762" /> : <SunIcon className="h-5 w-5" data-id="element-2763" />}
                        </button>}
                    </> :
            // Regular navigation for other pages
            <>
                      <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2764">
                        <HomeIcon className="h-4 w-4 mr-1" data-id="element-2765" />
                        Dashboard
                      </button>
                      <Link to="/find-teachers" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/find-teachers' ? 'text-deep-pink' : ''}`} data-id="element-2766">
                        <SearchIcon className="h-4 w-4 mr-1" data-id="element-2767" />
                        Find Teachers
                      </Link>
                      <Link to="/student-schedule" className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === '/student-schedule' ? 'text-deep-pink' : ''}`} onClick={e => handleSamePageNavigation('/student-schedule', e)} data-id="element-2768">
                        <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2769" />
                        My Schedule
                      </Link>
                      {/* Add NotificationCenter before dark/light mode toggle */}
                      <div className="px-2 py-1" data-id="element-2770">
                        <NotificationCenter darkMode={isDark} data-id="element-2771" />
                      </div>
                      {/* Dark/Light Mode Toggle for student pages and My Account pages */}
                      {isStudent && isOnStudentPage() || isOnMyAccountPage() && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2772">
                            {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2773" /> : <SunIcon className="h-5 w-5" data-id="element-2774" />}
                          </button>}
                    </> :
            // Teacher Navigation
            <>
                    <button onClick={handleDashboardClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === dashboardPath ? 'text-deep-pink' : ''}`} data-id="element-2775">
                      <HomeIcon className="h-4 w-4 mr-1" data-id="element-2776" />
                      Dashboard
                    </button>
                    <button onClick={handleProfileClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === profilePath ? 'text-deep-pink' : ''}`} data-id="element-2777">
                      <UserIcon className="h-4 w-4 mr-1" data-id="element-2778" />
                      Teacher Page
                    </button>
                    <button onClick={handleCalendarClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === calendarPath ? 'text-deep-pink' : ''}`} data-id="element-2779">
                      <CalendarIcon className="h-4 w-4 mr-1" data-id="element-2780" />
                      Calendar
                    </button>
                    <button onClick={handleMessagesClick} className={`text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors flex items-center ${location.pathname === messagesPath ? 'text-deep-pink' : ''}`} data-id="element-2781">
                      <MessageSquareIcon className="h-4 w-4 mr-1" data-id="element-2782" />
                      Messages
                    </button>
                    {/* Add NotificationCenter before dark/light mode toggle */}
                    <div className="px-2 py-1" data-id="element-2783">
                      <NotificationCenter darkMode={isDark} data-id="element-2784" />
                    </div>
                    {/* Dark/Light Mode Toggle — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={toggleTheme} className="text-white hover:text-gray-300 px-2 py-1 transition-colors flex items-center" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'} data-id="element-2785">
                        {isDark ? <MoonIcon className="h-5 w-5" data-id="element-2786" /> : <SunIcon className="h-5 w-5" data-id="element-2787" />}
                      </button>}
                  </>}

                {/* Account Dropdown for both user types */}
                <div className="relative account-dropdown" ref={accountDropdownRef} data-id="element-2788">
                  <button onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)} className="bg-[#3B82F6] border-2 border-[#3B82F6] hover:bg-[#2563EB] text-white text-sm py-1 px-3 rounded-lg transition-colors flex items-center" data-id="element-2789">
                    My Account
                    <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-2790" />
                  </button>
                  {isAccountDropdownOpen && <div className={`absolute right-0 mt-2 w-56 ${isDark ? 'bg-navy-800' : 'bg-white'} rounded-lg shadow-lg py-1 z-50`} data-id="element-2791">
                      {/* PHASE 1 FIX: Role-aware dropdown */}
                      {isStudent ?
                // STUDENT MENU: Only My Profile and Logout
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-profile')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-profile' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2792">
                            <UserIcon className="h-4 w-4 mr-2" data-id="element-2793" />
                            My Profile
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/account-settings')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/account-settings' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2794">
                            <SettingsIcon className="h-4 w-4 mr-2" data-id="element-2795" />
                            Account Settings
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2796"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2797">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2798" />
                            Logout
                          </button>
                        </> : isTeacher ?
                // TEACHER MENU: Full account management
                <>
                          <button onClick={() => handleAuthenticatedNavigation('/my-info')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/my-info' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2799">
                            <InfoIcon className="h-4 w-4 mr-2" data-id="element-2800" />
                            My Info
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/settings')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/settings' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2801">
                            <SettingsIcon className="h-4 w-4 mr-2" data-id="element-2802" />
                            Settings
                          </button>
                          <button onClick={() => handleAuthenticatedNavigation('/account-security')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/account-security' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2803">
                            <ShieldIcon className="h-4 w-4 mr-2" data-id="element-2804" />
                            Account Security
                          </button>
                          {/* Billing menu item temporarily hidden */}
                          <button onClick={() => handleAuthenticatedNavigation('/help-support')} className={`block w-full text-left px-4 py-2 ${location.pathname === '/help-support' ? `border-l-2 border-deep-pink font-semibold` : ''} ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2805">
                            <HelpCircleIcon className="h-4 w-4 mr-2" data-id="element-2806" />
                            Help & Support
                          </button>
                          <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-1`} data-id="element-2807"></div>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2808">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2809" />
                            Logout
                          </button>
                        </> :
                // STEP 2 FIX: Fallback when userType is null but user appears logged in
                // Ensures logout is always accessible even in desync edge cases
                <>
                          <button onClick={handleLogout} className={`block w-full text-left px-4 py-2 ${isDark ? 'text-white hover:bg-navy-900' : 'text-gray-800 hover:bg-gray-100'} flex items-center`} data-id="element-2810">
                            <LogOutIcon className="h-4 w-4 mr-2" data-id="element-2811" />
                            Logout
                          </button>
                        </>}
                    </div>}
                </div>
              </> : location.pathname === '/' ?
          // Public site navigation - homepage
          <>
                <button onClick={() => handleNavLinkClick('how-it-works')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2812">
                  How It Works
                </button>
                <button onClick={() => handleNavLinkClick('benefits')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2813">
                  Benefits
                </button>
                <button onClick={() => handleNavLinkClick('pricing')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2814">
                  Pricing
                </button>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <button onClick={() => handleNavLinkClick('tutorial-video')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2815">
                  Tutorial
                </button>
                <Link to="/contact" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2816">
                  Contact
                </Link>

                <button onClick={handleOpenSignInModal} className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2817">
                  Sign In
                </button>
              </> :
          // Public site navigation - other pages
          <>
                <button onClick={() => handleNavLinkClick('how-it-works')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2818">
                  How It Works
                </button>
                <button onClick={() => handleNavLinkClick('benefits')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2819">
                  Benefits
                </button>
                <button onClick={() => handleNavLinkClick('pricing')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2820">
                  Pricing
                </button>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                 to="/courses"
                 className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors"
                 >
                 Courses
                 </Link> */}
                <button onClick={() => handleNavLinkClick('tutorial-video')} className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2821">
                  Tutorial
                </button>
                <Link to="/contact" className="text-white hover:text-gray-300 px-2 py-1 text-sm font-medium transition-colors" data-id="element-2822">
                  Contact
                </Link>
                <button onClick={handleOpenSignInModal} className="text-deep-pink hover:text-burgundy px-2 py-1 text-sm font-medium transition-colors cursor-pointer bg-transparent border-none" data-id="element-2823">
                  Sign In
                </button>
              </>}
          </div>
        </div>
      </div>

      {/* Mobile menu - improved styling and touch targets */}
      {isMenuOpen && <div className={`md:hidden ${isDark ? 'bg-navy-800' : 'bg-white'} relative z-20`} ref={mobileMenuRef} onPointerDown={e => {
      // Stop propagation to prevent overlay from catching events
      e.stopPropagation();
      console.log('[MOBILE MENU] Menu container pointer down - propagation stopped');
    }} data-id="element-2824">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-inner" data-id="element-2825">
            {isLoggedIn ?
        // Dashboard mobile navigation for logged-in users
        <>
                {isStudent ?
          // Student mobile navigation - improved touch targets
          <>
                    <button onClick={() => {
              handleDashboardClick();
              setIsMenuOpen(false);
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2826">
                      <div className="flex items-center" data-id="element-2827">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2828" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/find-teachers')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/find-teachers' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2829">
                      <div className="flex items-center" data-id="element-2830">
                        <SearchIcon className="h-5 w-5 mr-3" data-id="element-2831" />
                        Find Teachers
                      </div>
                    </button>
                    <button onClick={() => handleAuthenticatedNavigation('/student-schedule')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/student-schedule' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2832">
                      <div className="flex items-center" data-id="element-2833">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2834" />
                        My Schedule
                      </div>
                    </button>
                    {/* Dark/Light Mode Toggle for mobile - Add for student pages and My Account pages */}
                    {(isStudent && isOnStudentPage() || isOnMyAccountPage()) && <button onClick={toggleTheme} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2835">
                        {isDark ? <>
                            <MoonIcon className="h-5 w-5 mr-3" data-id="element-2836" />
                            Dark Mode
                          </> : <>
                            <SunIcon className="h-5 w-5 mr-3" data-id="element-2837" />
                            Light Mode
                          </>}
                      </button>}
                  </> :
          // Teacher mobile navigation - Reordered links and improved touch targets
          <>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Dashboard button clicked');
              handleMenuAction(handleDashboardClick, 'Dashboard navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === dashboardPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2838">
                      <div className="flex items-center" data-id="element-2839">
                        <HomeIcon className="h-5 w-5 mr-3" data-id="element-2840" />
                        Dashboard
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] My Profile button clicked');
              handleMenuAction(handleProfileClick, 'Profile navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === profilePath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2841">
                      <div className="flex items-center" data-id="element-2842">
                        <UserIcon className="h-5 w-5 mr-3" data-id="element-2843" />
                        Teacher Page
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Calendar button clicked');
              handleMenuAction(handleCalendarClick, 'Calendar navigation');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === calendarPath ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2844">
                      <div className="flex items-center" data-id="element-2845">
                        <CalendarIcon className="h-5 w-5 mr-3" data-id="element-2846" />
                        Calendar
                      </div>
                    </button>
                    <button onClick={() => {
              console.log('[MOBILE MENU] Messages button clicked');
              handleAuthenticatedNavigation('/messages');
            }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/messages' || location.pathname === '/teacher-messages' ? isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2847">
                      <div className="flex items-center" data-id="element-2848">
                        <MessageSquareIcon className="h-5 w-5 mr-3" data-id="element-2849" />
                        Messages
                      </div>
                    </button>
                    {/* Dark/Light Mode Toggle for mobile — always show on teacher pages and My Account pages */}
                    {(isTeacher || isOnTeacherDashboardPage() || isOnMyAccountPage()) && <button onClick={() => {
              console.log('[MOBILE MENU] Theme toggle clicked');
              handleMenuAction(toggleTheme, 'Theme toggle');
            }} className={`flex items-center px-4 py-3 rounded-md text-base font-medium w-full ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2850">
                        {isDark ? <>
                            <MoonIcon className="h-5 w-5 mr-3" data-id="element-2851" />
                            Dark Mode
                          </> : <>
                            <SunIcon className="h-5 w-5 mr-3" data-id="element-2852" />
                            Light Mode
                          </>}
                      </button>}
                  </>}

                {/* Account dropdown items for mobile - PHASE 1 FIX: Role-aware */}
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} my-2 pt-2`} data-id="element-2853">
                  {isStudent ?
            // STUDENT MOBILE MENU: Only My Profile
            <>
                      <button onClick={() => {
                console.log('[MOBILE MENU] My Profile (account) clicked');
                handleAuthenticatedNavigation('/my-profile');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-profile' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2854">
                        <div className="flex items-center" data-id="element-2855">
                          <UserIcon className="h-5 w-5 mr-3" data-id="element-2856" />
                          My Profile
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Account Settings clicked');
                handleAuthenticatedNavigation('/account-settings');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/account-settings' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2857">
                        <div className="flex items-center" data-id="element-2858">
                          <SettingsIcon className="h-5 w-5 mr-3" data-id="element-2859" />
                          Account Settings
                        </div>
                      </button>
                    </> : isTeacher ?
            // TEACHER MOBILE MENU: Full account management
            <>
                      <button onClick={() => {
                console.log('[MOBILE MENU] My Info clicked');
                handleAuthenticatedNavigation('/my-info');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/my-info' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2860">
                        <div className="flex items-center" data-id="element-2861">
                          <InfoIcon className="h-5 w-5 mr-3" data-id="element-2862" />
                          My Info
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Settings clicked');
                handleAuthenticatedNavigation('/settings');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/settings' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2863">
                        <div className="flex items-center" data-id="element-2864">
                          <SettingsIcon className="h-5 w-5 mr-3" data-id="element-2865" />
                          Settings
                        </div>
                      </button>
                      <button onClick={() => {
                console.log('[MOBILE MENU] Account Security clicked');
                handleAuthenticatedNavigation('/account-security');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/account-security' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2866">
                        <div className="flex items-center" data-id="element-2867">
                          <ShieldIcon className="h-5 w-5 mr-3" data-id="element-2868" />
                          Account Security
                        </div>
                      </button>
                      {/* Billing menu item temporarily hidden */}
                      <button onClick={() => {
                console.log('[MOBILE MENU] Help & Support clicked');
                handleAuthenticatedNavigation('/help-support');
              }} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${location.pathname === '/help-support' ? isDark ? 'bg-navy-900 border-l-2 border-deep-pink font-semibold text-white' : 'bg-gray-100 border-l-2 border-deep-pink font-semibold text-gray-900' : isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2869">
                        <div className="flex items-center" data-id="element-2870">
                          <HelpCircleIcon className="h-5 w-5 mr-3" data-id="element-2871" />
                          Help & Support
                        </div>
                      </button>
                    </> : null}
                </div>

                {/* Logout button for both user types — STEP 2 FIX: Always visible regardless of userType */}
                <button onClick={handleLogout} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'} flex items-center`} data-id="element-2872">
                  <LogOutIcon className="h-5 w-5 mr-3" data-id="element-2873" />
                  Logout
                </button>
              </> :
        // Public site navigation - mobile
        <>
                <button onClick={() => handleNavLinkClick('how-it-works')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2874">
                  How It Works
                </button>
                <button onClick={() => handleNavLinkClick('benefits')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2875">
                  Benefits
                </button>
                <button onClick={() => handleNavLinkClick('pricing')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2876">
                  Pricing
                </button>
                <button onClick={() => handleNavLinkClick('tutorial-video')} className={`block w-full text-left px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-2877">
                  Tutorial
                </button>
                {/* TEMPORARILY HIDDEN: Courses page shows "Coming soon" placeholder */}
                {/* <Link
                  to="/courses"
                  className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                  onClick={() => setIsMenuOpen(false)}
                 >
                  Courses
                 </Link> */}
                <Link to="/contact" className={`block px-4 py-3 rounded-md text-base font-medium ${isDark ? 'text-gray-300 hover:text-white hover:bg-navy-700' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`} onClick={() => setIsMenuOpen(false)} data-id="element-2878">
                  Contact
                </Link>
              </>}
          </div>
        </div>}

      {/* Overlay for mobile when menu is open */}
      {isMenuOpen && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={() => {
      console.log('[MOBILE MENU] Overlay clicked - closing menu');
      setIsMenuOpen(false);
    }} onPointerDown={e => {
      console.log('[MOBILE MENU] Overlay pointer down');
    }} aria-hidden="true" data-id="element-2879"></div>}
    </nav>;
};